import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../components/TopBar";
import { api, fmtUSD } from "../lib/api";
import {
  ChevronLeft, ChevronRight, Wrench, FileText, Truck, Wheat, Heart,
  Calendar as CalIcon, ClipboardList, Sprout, Bell, Pill, CheckCircle2,
} from "lucide-react";

const EVENT_META = {
  "maintenance":      { color: "#BC4727", label: "Maintenance", Icon: Wrench, path: "/maintenance" },
  "invoice-due":      { color: "#1A4135", label: "Invoice Due", Icon: FileText, path: null /* business-specific */ },
  "work-order":       { color: "#5C4A2B", label: "Work Order",  Icon: ClipboardList, path: "/repair/work-orders" },
  "load-pickup":      { color: "#2C5C8C", label: "Load Pickup", Icon: Truck, path: "/trucking/loads" },
  "load-delivery":    { color: "#1F1E1B", label: "Delivery",    Icon: Truck, path: "/trucking/loads" },
  "harvest":          { color: "#7A6A2A", label: "Harvest",     Icon: Wheat, path: "/yields" },
  "harvest-ready":    { color: "#9D391E", label: "Harvest Ready", Icon: Sprout, path: "/fields" },
  "harvest-soon":     { color: "#C77E1A", label: "Harvest in 7d", Icon: Bell,  path: "/fields" },
  "livestock-care":   { color: "#9D391E", label: "Livestock",   Icon: Heart, path: "/livestock" },
  "medication-dose":  { color: "#5B2C82", label: "Med Dose",    Icon: Pill,  path: "/livestock" },
  "medication-clear": { color: "#1A4135", label: "Withdrawal Cleared", Icon: CheckCircle2, path: "/livestock" },
};

const BUSINESS_LABEL = {
  farm: "Farm",
  jk_repair: "J&K Repair",
  jk_trucking: "J&K Trucking",
};

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function fmtISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}
function buildGrid(cursor) {
  // Sun..Sat grid covering the entire visible month
  const first = startOfMonth(cursor);
  const startDow = first.getDay();
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startDow);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  return days;
}

export default function Calendar() {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(fmtISO(new Date()));
  const navigate = useNavigate();

  const grid = useMemo(() => buildGrid(cursor), [cursor]);
  const gridStart = fmtISO(grid[0]);
  const gridEnd = fmtISO(grid[grid.length - 1]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/calendar?start=${gridStart}&end=${gridEnd}`);
      setEvents(r.data.events || []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridStart, gridEnd]);

  const byDate = useMemo(() => {
    const m = {};
    for (const e of events) {
      if (!e.date) continue;
      (m[e.date] = m[e.date] || []).push(e);
    }
    return m;
  }, [events]);

  const monthLabel = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });
  const todayISO = fmtISO(new Date());
  const selectedEvents = byDate[selected] || [];

  const goToEvent = (e) => {
    const meta = EVENT_META[e.type];
    if (!meta) return;
    if (e.type === "invoice-due") {
      // Route to the correct business invoices listing
      const biz = e.business_id;
      if (biz === "jk_repair") navigate("/repair/invoices");
      else if (biz === "jk_trucking") navigate("/trucking/invoices");
      else navigate("/reports");
      return;
    }
    if (meta.path) navigate(meta.path);
  };

  return (
    <div>
      <TopBar
        title="Calendar"
        subtitle="Every business in one view — harvests, maintenance, invoices, repairs, hauls."
        actions={
          <div className="flex items-center gap-2 mt-6" data-testid="calendar-controls">
            <button data-testid="calendar-prev" onClick={() => setCursor(addMonths(cursor, -1))} className="border border-[#1F1E1B] p-2 hover:bg-[#1F1E1B] hover:text-white transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button data-testid="calendar-today" onClick={() => { const t = startOfMonth(new Date()); setCursor(t); setSelected(todayISO); }} className="border border-[#1F1E1B] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors">
              Today
            </button>
            <button data-testid="calendar-next" onClick={() => setCursor(addMonths(cursor, 1))} className="border border-[#1F1E1B] p-2 hover:bg-[#1F1E1B] hover:text-white transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="ml-3 font-display text-2xl font-black tabular" data-testid="calendar-month-label">{monthLabel}</div>
          </div>
        }
      />

      {/* Legend */}
      <div className="bg-white border border-[#1F1E1B] p-3 mb-4 flex flex-wrap gap-3" data-testid="calendar-legend">
        {Object.entries(EVENT_META).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-xs">
            <span className="w-3 h-3" style={{ background: v.color }} />
            <v.Icon className="w-3 h-3" /> {v.label}
          </span>
        ))}
        <span className="ml-auto text-xs text-[#66645E]">
          {loading ? "Loading…" : `${events.length} events this view`}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Month grid */}
        <div className="lg:col-span-2 bg-white border border-[#1F1E1B]" data-testid="calendar-grid">
          {/* DOW header */}
          <div className="grid grid-cols-7 border-b border-[#1F1E1B] bg-[#F5F4F0]">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] py-2 text-center">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7" style={{ gridAutoRows: "minmax(96px, 1fr)" }}>
            {grid.map((d, idx) => {
              const iso = fmtISO(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = iso === todayISO;
              const isSelected = iso === selected;
              const items = byDate[iso] || [];
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelected(iso)}
                  data-testid={`cal-day-${iso}`}
                  className={`relative text-left border-r border-b border-[#E4E1D5] last:border-r-0 p-1.5 hover:bg-[#FAF7EC] transition-colors
                    ${inMonth ? "" : "bg-[#FAF8F2] opacity-60"}
                    ${isSelected ? "ring-2 ring-inset ring-[#BC4727]" : ""}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs tabular ${isToday ? "bg-[#1F1E1B] text-white px-1.5 py-0.5 font-bold" : "text-[#1F1E1B] font-semibold"}`}>
                      {d.getDate()}
                    </span>
                    {items.length > 0 && (
                      <span className="text-[9px] font-bold text-[#66645E]">{items.length}</span>
                    )}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {items.slice(0, 3).map((e, i) => {
                      const meta = EVENT_META[e.type] || { color: "#66645E", label: "" };
                      return (
                        <div
                          key={i}
                          className="text-[10px] text-white px-1 py-0.5 truncate"
                          style={{ background: meta.color }}
                          title={e.title}
                        >
                          {e.title}
                        </div>
                      );
                    })}
                    {items.length > 3 && (
                      <div className="text-[10px] text-[#66645E] font-bold">+{items.length - 3} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Day detail panel */}
        <div className="bg-white border border-[#1F1E1B] p-4" data-testid="calendar-day-detail">
          <div className="flex items-center gap-2 mb-3">
            <CalIcon className="w-4 h-4" />
            <div className="font-display text-lg font-bold">
              {new Date(selected + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" })}
            </div>
          </div>

          {selectedEvents.length === 0 ? (
            <div className="text-sm text-[#66645E] border border-dashed border-[#1F1E1B] p-6 text-center" data-testid="calendar-day-empty">
              Nothing scheduled.
            </div>
          ) : (
            <div className="space-y-2" data-testid="calendar-day-events">
              {selectedEvents.map((e, i) => {
                const meta = EVENT_META[e.type] || { color: "#66645E", label: e.type, Icon: CalIcon };
                const Icon = meta.Icon;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goToEvent(e)}
                    data-testid={`calendar-event-${i}`}
                    className="w-full text-left border border-[#1F1E1B] p-3 hover:bg-[#FAF7EC] transition-colors flex items-start gap-3"
                  >
                    <div className="w-1 self-stretch" style={{ background: meta.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-3.5 h-3.5" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#66645E]">{meta.label}</span>
                        {e.business_id && (
                          <span className="text-[9px] text-[#66645E] uppercase tracking-wider">· {BUSINESS_LABEL[e.business_id] || e.business_id}</span>
                        )}
                      </div>
                      <div className="text-sm font-semibold truncate">{e.title}</div>
                      <div className="flex items-center gap-3 text-[11px] text-[#66645E] mt-0.5">
                        {e.status && <span className="uppercase tracking-wider">{e.status}</span>}
                        {typeof e.amount === "number" && e.amount > 0 && <span className="tabular">{fmtUSD(e.amount)}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
